import { Box, Chip, LinearProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams } from 'src/mentor/services'

const PRACTITIONER_ASSESSMENT_ACCESS_DETAILS_URL = '/api/secure/practitioner/assessments/access/details'

function parsePractitionerAccessResponse(response) {
  // API may wrap payload in response.data.data
  const payload = response?.data?.data ?? response?.data
  if (!payload) return null
  const access = payload?.assessmentAccess ?? {}
  return {
    allowedToAssess: access?.allowedToAssess ?? payload?.allowedToAssess ?? false,
    assessmentListAllowed: access?.assessmentListAllowed ?? payload?.assessmentListAllowed ?? [],
    assessmentLists: access?.assessmentLists ?? payload?.assessmentLists ?? [],
  }
}

/** Returns refs that have no matching assessment (for "assessment missing / deleted" warning). */
function getUnresolvedAssessmentRefs(assessmentRefs, assessments) {
  if (!Array.isArray(assessmentRefs) || assessmentRefs.length === 0) return []
  const list = Array.isArray(assessments) ? assessments : []
  return assessmentRefs.filter((ref) => {
    const value = ref?.value
    if (value == null) return true
    const isId = ref?.type === 'id'
    return !list.some(
      (a) => (isId ? a?.id === value : (a?.slug === value || a?.id === value)),
    )
  })
}

export default function PractitionerAssessmentAccessView() {
  const [loading, setLoading] = useState(true)
  const [allowedToAssess, setAllowedToAssess] = useState(false)
  const [assessmentListAllowed, setAssessmentListAllowed] = useState([])
  const [assessmentLists, setAssessmentLists] = useState([])

  const fetchAssessmentAccessDetails = useCallback(async () => {
    setLoading(true)
    const response = await getMPDataWithoutParams(PRACTITIONER_ASSESSMENT_ACCESS_DETAILS_URL)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const parsed = parsePractitionerAccessResponse(response)
      if (parsed) {
        setAllowedToAssess(!!parsed.allowedToAssess)
        setAssessmentListAllowed(
          Array.isArray(parsed.assessmentListAllowed) ? parsed.assessmentListAllowed : [],
        )
        setAssessmentLists(Array.isArray(parsed.assessmentLists) ? parsed.assessmentLists : [])
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAssessmentAccessDetails()
  }, [fetchAssessmentAccessDetails])

  return (
    <div style={{ width: '100%', maxWidth: '768px', paddingBottom: '80px' }}>
      {loading && <LinearProgress />}

      <Typography variant="h6" className="mb-3 fw-bold">
        Assessment Access
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-3">
        Your assessment access is managed by your organization. Below is your current access (read-only).
      </Typography>

      {!loading && (
        <>
          <Box className="mb-4">
            <Typography variant="subtitle2" className="mb-1">
              Allowed to run assessments
            </Typography>
            <Box className="d-flex align-items-center gap-2">
              <Chip
                label={allowedToAssess ? 'Yes' : 'No'}
                color={allowedToAssess ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
              {!allowedToAssess && (
                <Typography variant="caption" color="text.secondary">
                  You cannot run assessments. Contact support if you need access.
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="mb-4">
            <Typography variant="subtitle2" className="mb-2">
              Assessment lists assigned to you
            </Typography>
            {assessmentListAllowed.length === 0 && assessmentLists.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No assessment lists assigned.
              </Typography>
            ) : (
              <>
                {assessmentListAllowed.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {assessmentListAllowed.map((ref, index) => (
                      <Chip
                        key={`${ref?.type}-${ref?.value}-${index}`}
                        label={ref?.title ?? ref?.value ?? '—'}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                {assessmentLists.length > 0 && (
                  <Typography variant="subtitle2" className="mb-2 fw-bold">
                    Lists and assessments
                  </Typography>
                )}
                {assessmentLists.map((list) => {
                  const unresolvedRefs = getUnresolvedAssessmentRefs(
                    list?.assessmentRefs,
                    list?.assessments,
                  )
                  return (
                    <Box
                      key={list.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" className="fw-medium mb-1">
                        {list.title ?? list.slug ?? list.id}
                      </Typography>
                      {Array.isArray(list.assessments) && list.assessments.length > 0 ? (
                        <Box
                          component="ul"
                          sx={{ m: 0, pl: 2.5, fontSize: '0.875rem', color: 'text.secondary' }}
                        >
                          {list.assessments.map((a) => (
                            <li key={a.id ?? a.slug}>{a.title ?? a.slug ?? a.id}</li>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No assessments in this list.
                        </Typography>
                      )}
                      {unresolvedRefs.length > 0 && (
                        <Typography variant="caption" color="warning.main" className="d-block mt-1">
                          Assessment missing / deleted: {unresolvedRefs.map((r) => r?.title ?? r?.value).filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </>
            )}
          </Box>
        </>
      )}
    </div>
  )
}
